import { FileText, Folder } from "lucide-react";
import { ChangeEventHandler, useCallback, useState } from "react";
import { Button } from "../../components/ui/button/button";
import { Card } from "../../components/ui/card/card";
import { Input } from "../../components/ui/input/input";
import { Label } from "../../components/ui/label/label";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../components/ui/toggle-group/toggle-group";
import { Document } from "../../odata/document/DocumentModel";
import { useAllDocuments } from "../../queries/documents/use-all-documents";
import { useUploadDocument } from "../../queries/documents/use-upload-document";
import { useTranslation } from "react-i18next";
import { QueryError } from "@/components/ui/query-error/query-error";

export interface DocumentListProps {
  onValueChange: (values: string[]) => void;
  value: string[];
  onCancel?: VoidFunction;
  onSelect: (values: string[]) => void;
}

export function DocumentList({
  onValueChange,
  value,
  onCancel,
  onSelect,
}: DocumentListProps) {
  const { t } = useTranslation();
  const { documents, error, refetch } = useAllDocuments();
  const { uploadDocument, isPending } = useUploadDocument();
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);

  const handleFileUpload: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!e.target.files) return;

      uploadDocument(e.target.files[0], {
        onSuccess: (data) => {
          setUploadedFileIds((x) => [...x, data.Id!]);
          onValueChange([...value, data.Id!]);
        },
      });
    },
    [onValueChange, uploadDocument, value]
  );

  const handleCancel = useCallback(() => {
    onValueChange([]);
    onCancel?.();
  }, [onCancel, onValueChange]);

  const handleSelect = useCallback(() => {
    onSelect(value);
  }, [onSelect, value]);

  return (
    <>
      <div className="overflow-auto flex flex-col h-full p-5">
        <Label htmlFor="document">
          <Button
            asChild
            size="default"
            className="w-full mb-5"
            loading={isPending}
            disabled={isPending}
          >
            <span className="inline-flex gap-2">
              <Folder className="h-4 w-4" />
              {t("documentSelector.list.browse")}
            </span>
          </Button>
        </Label>
        <Input
          id="document"
          type="file"
          className="hidden"
          onChange={handleFileUpload}
        />

        {error && (
          <QueryError
            errors={[error]}
            resource={t("documents.title")}
            retries={[refetch]}
          />
        )}

        <ToggleGroup
          type="multiple"
          onValueChange={onValueChange}
          value={value}
        >
          {reorderFilesByIds(documents ?? [], uploadedFileIds)?.map((x) => (
            <ToggleGroupItem value={x.Id} key={x.Id} className="basis-full">
              <Card className="px-5 py-1">
                <FileText className="h-5 w-5" strokeWidth={1} />
              </Card>
              {x.Name}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <div className="p-5 flex justify-between">
        {onCancel && (
          <Button size="default" variant="secondary" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
        )}

        <Button
          size="default"
          onClick={handleSelect}
          disabled={value.length === 0}
        >
          {t("documentSelector.list.select", {
            x: value.length > 0 ? value.length : "",
          })}
        </Button>
      </div>
    </>
  );
}

function reorderFilesByIds(files: Document[], fileIds: string[]): Document[] {
  return files.sort((a, b) => {
    const indexA = fileIds.indexOf(a.Id);
    const indexB = fileIds.indexOf(b.Id);

    // Move files with IDs in `fileIds` to the front in the specified order
    if (indexA >= 0 && indexB >= 0) {
      return indexA - indexB;
    } else if (indexA >= 0) {
      return -1; // a should come before b
    } else if (indexB >= 0) {
      return 1; // b should come before a
    } else {
      return 0; // keep original order for files not in fileIds
    }
  });
}
